/**
 * Find-my-strategy / compute retry — composes submit + compute + status.
 *
 * Belongs here: resolve sequence for the review CTA.
 * Does not belong here: session answers, leave plans, or review out-of-sync autosubmit.
 */
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '../../../../../state-management/contexts/ToastContext'
import type { StrategyQuestionnaireAnswersMap } from '../../shared/types/questionnaire.types'
import type { AllocationRiskProfile } from '../../types/allocation.types'
import {
  COMPUTE_RISK_PROFILE_MIN_PENDING_MS,
  computeRiskProfileWithRetries,
  useComputeRiskProfileMutation
} from '../../api/useComputeRiskProfileMutation'
import type { SubmitStrategyAnswersVars } from '../../api/useSubmitStrategyAnswersMutation'
import { sleep } from '../../api/awaitWithMinDuration'
import {
  EXPERT_REVIEW_SAVE_ERROR_TOAST,
  EXPERT_REVIEW_STATUS_REFRESH_ERROR_TOAST
} from '../../api/runExpertReviewSubmit'
import { refetchQuestionnaireStatusWithRetries } from '../../api/useQuestionnaireStatusQuery'

/**
 * Outcome of Find-my-strategy / compute-only retry — UI owns navigation.
 * Switch on enum members; never compare raw strings.
 */
export enum StrategyResolveResult {
  Success = 'success',
  SubmitFailed = 'submit_failed',
  ComputeFailed = 'compute_failed'
}

type UseStrategyResolveArgs = {
  questionnaireId: string
  committedAnswers: StrategyQuestionnaireAnswersMap
  committedAnswersVersion: number
  isAnswersVersionOutOfSync: boolean
  submitAnswers: (vars: SubmitStrategyAnswersVars) => Promise<void>
  setRiskProfile: (riskProfile: AllocationRiskProfile | null) => void
}

/** Resolve + retry for StrategyReviewPage — page passes session submit APIs. */
export function useStrategyResolve({
  questionnaireId,
  committedAnswers,
  committedAnswersVersion,
  isAnswersVersionOutOfSync,
  submitAnswers,
  setRiskProfile
}: UseStrategyResolveArgs) {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  const riskProfileMutation = useComputeRiskProfileMutation({
    onSuccess: (nextRiskProfile) => setRiskProfile(nextRiskProfile)
  })

  /**
   * Find-my-strategy: POST when out of sync, compute risk profile, then refresh /status.
   * Status also runs after compute failure so hub is not stuck pending after a successful submit.
   */
  async function resolveStrategy(): Promise<StrategyResolveResult> {
    const startedAtMs = Date.now()
    const answers = committedAnswers

    if (!questionnaireId || Object.keys(answers).length === 0) {
      showToast({ message: EXPERT_REVIEW_SAVE_ERROR_TOAST, duration: 2500 })
      return StrategyResolveResult.SubmitFailed
    }

    if (isAnswersVersionOutOfSync) {
      try {
        await submitAnswers({
          questionnaireId,
          answers,
          answersVersion: committedAnswersVersion
        })
      } catch {
        return StrategyResolveResult.SubmitFailed
      }
    }

    const profile = await computeRiskProfileWithRetries(() => riskProfileMutation.mutateAsync())
    if (!profile) {
      try {
        await refetchQuestionnaireStatusWithRetries(queryClient)
      } catch {
        showToast({ message: EXPERT_REVIEW_STATUS_REFRESH_ERROR_TOAST, duration: 2500 })
      }
      return StrategyResolveResult.ComputeFailed
    }

    try {
      await refetchQuestionnaireStatusWithRetries(queryClient)
    } catch {
      showToast({ message: EXPERT_REVIEW_STATUS_REFRESH_ERROR_TOAST, duration: 2500 })
      return StrategyResolveResult.ComputeFailed
    }

    const remainingHoldMs = Math.max(
      0,
      COMPUTE_RISK_PROFILE_MIN_PENDING_MS - (Date.now() - startedAtMs)
    )
    if (remainingHoldMs > 0) await sleep(remainingHoldMs)

    return StrategyResolveResult.Success
  }

  /** Retry compute only (after submit succeeded) — refresh /status after compute. */
  async function retryComputeRiskProfile(): Promise<
    StrategyResolveResult.Success | StrategyResolveResult.ComputeFailed
  > {
    const profile = await computeRiskProfileWithRetries(() => riskProfileMutation.mutateAsync())
    if (!profile) return StrategyResolveResult.ComputeFailed
    try {
      await refetchQuestionnaireStatusWithRetries(queryClient)
    } catch {
      showToast({ message: EXPERT_REVIEW_STATUS_REFRESH_ERROR_TOAST, duration: 2500 })
      return StrategyResolveResult.ComputeFailed
    }
    return StrategyResolveResult.Success
  }

  return { resolveStrategy, retryComputeRiskProfile }
}
